import { useEffect, useRef, useState } from "react";
import { crmSupabase } from "../../lib/crm-client";
import {
  updateTouchpoint,
  duplicateTouchpoint,
  createTouchpoint,
  uploadVideoAndThumb,
  captureAndUploadThumbnailAt,
  uploadAvatar,
} from "../../lib/crm-mutations";
import { TouchpointPreview } from "./TouchpointPreview";
import { BLOCKS_SCHEMA, getBlock, type TpStyle } from "./touchpoint-blocks";

type Touchpoint = {
  id: string;
  number: string;
  style: TpStyle;
  title: string;
  body_text: string | null;
  body_html: string | null;
  body_template: { blocks?: Record<string, string>; [k: string]: unknown } | null;
  video_url: string | null;
  video_thumb_url: string | null;
  avatar_url: string | null;
  cta_label: string | null;
  channel: "email" | "whatsapp";
  status: "draft" | "ready" | "archived";
};

type EligibleLead = {
  id: string;
  full_name: string;
  is_estrella: boolean;
  category: { name: string; color_hex: string } | null;
};

const STYLE_LABEL: Record<string, string> = {
  plain: "PLAIN TEXT",
  wanted: "NEWSLETTER · WANTED",
  drama: "NEWSLETTER · DRAMA",
  sticker: "NEWSLETTER · STICKER",
  info: "FORMAT INFO",
};

const STYLE_BADGE: Record<string, string> = {
  plain: "bg-charcoal text-white",
  wanted: "bg-charcoal text-lemonade",
  drama: "bg-black text-lemonade",
  sticker: "bg-lemonade text-black",
  info: "bg-concrete text-lemonade",
};

export default function CrmTouchpoints() {
  const [items, setItems] = useState<Touchpoint[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tab, setTab] = useState<"preview" | "edit" | "targets">("preview");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    reload();
  }, []);

  const reload = () => {
    if (!crmSupabase) {
      setLoading(false);
      return;
    }
    crmSupabase
      .from("touchpoint")
      .select(
        "id,number,style,title,body_text,body_html,body_template,video_url,video_thumb_url,avatar_url,cta_label,channel,status",
      )
      .neq("status", "archived")
      .order("number", { ascending: true })
      .then(({ data, error }) => {
        if (error) console.error(error);
        const list = (data as Touchpoint[]) ?? [];
        setItems(list);
        if (!activeId && list.length > 0) setActiveId(list[0].id);
        setLoading(false);
      });
  };

  const active = items.find((t) => t.id === activeId);

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateTouchpoint(id);
      reload();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreate = async () => {
    const num = prompt("Numéro du nouveau touchpoint (ex: '6') :");
    if (!num) return;
    const title = prompt("Titre du touchpoint :", "Nouveau touchpoint");
    if (!title) return;
    try {
      const created = await createTouchpoint({
        number: num,
        style: "plain",
        title,
        channel: "email",
        body_text: "Hello {prénom},\n\n…\n\nJérémy",
      });
      reload();
      if (created) setActiveId((created as any).id);
      setTab("edit");
    } catch (e) {
      console.error(e);
      alert("Erreur de création");
    }
  };

  if (loading) {
    return (
      <main className="px-10 py-8 max-w-[1400px] mx-auto font-mono text-sm text-concrete">
        Chargement…
      </main>
    );
  }

  return (
    <main className="px-10 py-8 max-w-[1400px] mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h2 className="font-sans text-4xl font-bold tracking-tight text-charcoal">
          Touchpoints
        </h2>
        <span className="font-mono text-xs uppercase text-concrete">
          {items.length} templates · clic pour prévisualiser
        </span>
      </header>

      <div className="grid grid-cols-[340px_1fr] gap-4">
        {/* Sidebar list */}
        <aside className="flex flex-col gap-2">
          {items.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveId(t.id)}
              className={`text-left p-3 rounded-lg border transition ${
                t.id === activeId
                  ? "border-charcoal bg-white shadow-sm"
                  : "border-stone bg-white hover:border-charcoal"
              }`}
            >
              <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                <span className="font-mono text-[11px] font-bold text-charcoal">
                  TP{t.number}
                </span>
                <span
                  className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded ${
                    t.status === "ready"
                      ? "bg-green-100 text-green-800"
                      : "bg-orange-100 text-orange-800"
                  }`}
                >
                  {t.status}
                </span>
                <span
                  className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded ${
                    t.channel === "email"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {t.channel}
                </span>
                <span
                  className={`font-mono text-[9px] uppercase px-1.5 py-0.5 rounded ${STYLE_BADGE[t.style]}`}
                >
                  {STYLE_LABEL[t.style]}
                </span>
              </div>
              <div className="text-sm text-charcoal font-semibold truncate">
                {t.title}
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDuplicate(t.id);
                  }}
                  className="font-mono text-[10px] uppercase px-2 py-1 rounded border border-stone bg-white text-concrete hover:border-charcoal hover:text-charcoal"
                  title="Dupliquer"
                >
                  ⎘ Dupliquer
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveId(t.id);
                    setTab("edit");
                  }}
                  className="font-mono text-[10px] uppercase px-2 py-1 rounded border border-stone bg-white text-concrete hover:border-charcoal hover:text-charcoal"
                  title="Éditer"
                >
                  ✎ Éditer
                </button>
              </div>
            </button>
          ))}
          <button
            onClick={handleCreate}
            className="p-3 rounded-lg border-2 border-dashed border-stone text-charcoal hover:border-charcoal hover:bg-white text-sm font-mono uppercase"
          >
            + Créer un touchpoint
          </button>
        </aside>

        {/* Detail panel */}
        <section className="bg-white border border-stone rounded-lg">
          {active ? (
            <>
              <nav className="flex border-b border-stone">
                {(["preview", "edit", "targets"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                      tab === t
                        ? "text-charcoal border-charcoal -mb-px"
                        : "text-concrete border-transparent hover:text-charcoal"
                    }`}
                  >
                    {t === "preview"
                      ? "Aperçu"
                      : t === "edit"
                        ? "Éditer"
                        : "Cibles"}
                  </button>
                ))}
              </nav>
              <div className="p-6">
                {tab === "preview" && <PreviewTab tp={active} />}
                {tab === "edit" && (
                  <EditTab
                    tp={active}
                    onSaved={(patch) => {
                      setItems((items) =>
                        items.map((x) =>
                          x.id === active.id ? { ...x, ...patch } : x,
                        ),
                      );
                    }}
                  />
                )}
                {tab === "targets" && <TargetsTab tpId={active.id} />}
              </div>
            </>
          ) : (
            <div className="p-6 font-mono text-sm text-concrete">
              Sélectionne un touchpoint à gauche.
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function PreviewTab({ tp }: { tp: Touchpoint }) {
  return (
    <div className="space-y-3">
      <div className="font-mono text-[10px] uppercase text-concrete flex justify-between items-baseline">
        <span>Aperçu — style {tp.style} · variables remplacées par "Bruno"</span>
        <span>{tp.channel === "whatsapp" ? "📱 WhatsApp" : "✉ Email"}</span>
      </div>
      <TouchpointPreview
        tp={tp}
        vars={{
          prenom: "Bruno",
          sujet1: "Vivatech",
          sujet2: "startups françaises",
          sujet3: "AI européen",
        }}
      />
    </div>
  );
}

function EditTab({
  tp,
  onSaved,
}: {
  tp: Touchpoint;
  onSaved: (patch: Partial<Touchpoint>) => void;
}) {
  const schema = BLOCKS_SCHEMA[tp.style] ?? [];

  // Initialize one state value per block + the technical fields
  const initBlocks = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const def of schema) {
      out[def.key] = getBlock(tp.style, tp.body_template?.blocks, def.key);
    }
    // "title" block always mirrors tp.title for consistency
    if (!out.title) out.title = tp.title;
    return out;
  };

  const [bkValues, setBkValues] = useState<Record<string, string>>(initBlocks);
  const [videoUrl, setVideoUrl] = useState(tp.video_url ?? "");
  const [ctaLabel, setCtaLabel] = useState(tp.cta_label ?? "");
  const [channel, setChannel] = useState<"email" | "whatsapp">(tp.channel);
  const [status, setStatus] = useState<"draft" | "ready" | "archived">(tp.status);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Refs to focus the proper input when "Insert {prénom}" is clicked
  const inputRefs = useRef<Record<string, HTMLInputElement | HTMLTextAreaElement | null>>({});

  useEffect(() => {
    setBkValues(initBlocks());
    setVideoUrl(tp.video_url ?? "");
    setCtaLabel(tp.cta_label ?? "");
    setChannel(tp.channel);
    setStatus(tp.status);
    setSavedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tp.id]);

  const setBlock = (key: string, value: string) => {
    setBkValues((v) => ({ ...v, [key]: value }));
  };

  const insertVarAt = (key: string, variable: string) => {
    const el = inputRefs.current[key];
    if (!el) return;
    const before = bkValues[key].slice(0, el.selectionStart ?? bkValues[key].length);
    const after = bkValues[key].slice(el.selectionEnd ?? bkValues[key].length);
    const next = before + variable + after;
    setBlock(key, next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = (el.selectionStart ?? 0) + variable.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleSave = async () => {
    // body_template.blocks holds all overrides; body_text gets the "body" block
    // for the plain style (kept for compatibility with the rest of the app).
    const blocks: Record<string, string> = { ...bkValues };
    const newTitle = blocks.title ?? tp.title;
    const patch = {
      title: newTitle,
      body_text: blocks.body ?? blocks.bubble ?? tp.body_text ?? "",
      video_url: videoUrl || null,
      cta_label: ctaLabel || null,
      channel,
      status,
      body_template: {
        ...(tp.body_template ?? {}),
        blocks,
      },
    };
    try {
      await updateTouchpoint(tp.id, patch);
      onSaved(patch as any);
      setSavedAt(new Date().toLocaleTimeString("fr-FR"));
    } catch (e) {
      console.error(e);
      alert("Erreur sauvegarde — voir console");
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-[11px] text-concrete bg-ivory rounded p-2 font-mono">
        Style : <strong className="text-charcoal">{tp.style}</strong> · {schema.length} bloc{schema.length > 1 ? "s" : ""} éditable{schema.length > 1 ? "s" : ""}.
        Bouton "+ &#123;prénom&#125;" pour insérer la variable au curseur dans les blocs qui supportent les variables.
      </div>

      {schema.map((def) => (
        <div key={def.key} className="border border-stone rounded-lg p-3 bg-white">
          <div className="flex items-baseline justify-between mb-1">
            <label className="font-mono text-[10px] uppercase text-concrete">
              {def.label}
              {def.vars && (
                <span className="ml-2 text-ash">[supporte les variables]</span>
              )}
            </label>
            {def.help && (
              <span className="font-mono text-[9px] text-ash italic">{def.help}</span>
            )}
          </div>
          {def.type === "textarea" ? (
            <textarea
              ref={(el) => {
                inputRefs.current[def.key] = el;
              }}
              value={bkValues[def.key] ?? ""}
              onChange={(e) => setBlock(def.key, e.target.value)}
              rows={def.rows ?? 3}
              className="w-full px-2 py-1.5 bg-ivory border border-stone rounded text-sm font-mono focus:outline-none focus:border-charcoal resize-y"
            />
          ) : (
            <input
              ref={(el) => {
                inputRefs.current[def.key] = el;
              }}
              type="text"
              value={bkValues[def.key] ?? ""}
              onChange={(e) => setBlock(def.key, e.target.value)}
              className="w-full px-2 py-1.5 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal"
            />
          )}
          {def.vars && (
            <div className="flex gap-1 mt-1.5 flex-wrap">
              <button
                type="button"
                onClick={() => insertVarAt(def.key, "{prénom}")}
                className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded border border-stone bg-white text-concrete hover:bg-ivory hover:border-charcoal"
              >
                + {"{prénom}"}
              </button>
              <button
                type="button"
                onClick={() => insertVarAt(def.key, "{nom}")}
                className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded border border-stone bg-white text-concrete hover:bg-ivory hover:border-charcoal"
              >
                + {"{nom}"}
              </button>
              {tp.style === "info" && (
                <>
                  <button
                    type="button"
                    onClick={() => insertVarAt(def.key, "{sujet 1}")}
                    className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded border border-stone bg-white text-concrete hover:bg-ivory hover:border-charcoal"
                  >
                    + {"{sujet 1}"}
                  </button>
                  <button
                    type="button"
                    onClick={() => insertVarAt(def.key, "{sujet 2}")}
                    className="font-mono text-[9px] uppercase px-1.5 py-0.5 rounded border border-stone bg-white text-concrete hover:bg-ivory hover:border-charcoal"
                  >
                    + {"{sujet 2}"}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Upload vidéo S3 (auto-thumb à 1s) */}
      <VideoUploadCard
        tp={tp}
        videoUrl={videoUrl}
        onUploaded={(v) => {
          setVideoUrl(v.videoUrl);
          onSaved({ video_url: v.videoUrl, video_thumb_url: v.thumbUrl } as any);
        }}
      />

      {/* Thumbnail picker (apparaît seulement si vidéo uploadée) */}
      {tp.video_url && (
        <ThumbnailPickerCard
          tp={tp}
          onPicked={(thumbUrl) =>
            onSaved({ video_thumb_url: thumbUrl } as any)
          }
        />
      )}

      {/* Upload avatar (photo dans le rond en bas-gauche de la video card) */}
      <AvatarUploadCard
        tp={tp}
        onUploaded={(avatarUrl) =>
          onSaved({ avatar_url: avatarUrl } as any)
        }
      />

      {/* Technical fields (always shown, separate from blocks) */}
      <div className="border-t border-stone pt-4 mt-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase text-concrete">
            URL vidéo (auto-rempli après upload)
          </span>
          <input
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://…"
            className="mt-1 w-full px-3 py-2 bg-ivory border border-stone rounded text-sm font-mono focus:outline-none focus:border-charcoal"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase text-concrete">CTA</span>
          <input
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder="Voir la vidéo →"
            className="mt-1 w-full px-3 py-2 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal"
          />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase text-concrete">Canal</span>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as any)}
            className="mt-1 w-full px-3 py-2 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal"
          >
            <option value="email">Email</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase text-concrete">Statut</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="mt-1 w-full px-3 py-2 bg-ivory border border-stone rounded text-sm focus:outline-none focus:border-charcoal"
          >
            <option value="draft">Draft</option>
            <option value="ready">Ready</option>
            <option value="archived">Archived</option>
          </select>
        </label>
      </div>

      <div className="flex gap-2 items-center pt-2 sticky bottom-2 bg-ivory/95 p-2 rounded border border-stone shadow-sm">
        <button
          onClick={handleSave}
          className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black"
        >
          Sauvegarder
        </button>
        {savedAt && (
          <span className="font-mono text-[10px] text-green-700">
            ✓ sauvegardé à {savedAt}
          </span>
        )}
      </div>
    </div>
  );
}

function VideoUploadCard({
  tp,
  videoUrl,
  onUploaded,
}: {
  tp: Touchpoint;
  videoUrl: string;
  onUploaded: (v: { videoUrl: string; thumbUrl: string }) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      alert("Le fichier doit être une vidéo (.mp4, .mov, .webm)");
      return;
    }
    // S3 has no practical per-file limit ; cap at 2 GB to avoid runaway uploads
    if (file.size > 2 * 1024 * 1024 * 1024) {
      alert(
        `Fichier trop gros (${(file.size / 1024 / 1024).toFixed(1)} MB · limite 2 GB).`,
      );
      return;
    }
    setBusy(true);
    setStatus("Préparation…");
    try {
      const result = await uploadVideoAndThumb(file, tp.id, (label) =>
        setStatus(label),
      );
      onUploaded(result);
      setStatus(`✓ Vidéo uploadée (${(file.size / 1024 / 1024).toFixed(1)} MB)`);
    } catch (err: any) {
      console.error(err);
      setStatus(`❌ ${err?.message ?? "Upload échoué"}`);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="border border-stone rounded-lg p-3 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[10px] uppercase text-concrete">
          Vidéo du touchpoint
        </span>
        <span className="font-mono text-[9px] text-ash italic">
          .mp4/.mov/.webm · stocké sur S3 · thumbnail extraite auto
        </span>
      </div>

      {tp.video_thumb_url ? (
        <div className="flex gap-3 items-start mb-2">
          <img
            src={tp.video_thumb_url}
            alt="thumbnail"
            className="w-40 h-24 object-cover rounded border border-stone"
          />
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <a
              href={tp.video_url ?? "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-charcoal hover:underline truncate"
            >
              {tp.video_url}
            </a>
            <span className="text-[10px] font-mono text-ash">
              Cliquer le bouton ci-dessous pour remplacer
            </span>
          </div>
        </div>
      ) : videoUrl ? (
        <div className="text-xs font-mono text-concrete mb-2 truncate">
          URL actuelle : {videoUrl}
        </div>
      ) : (
        <div className="text-xs font-mono text-ash italic mb-2">
          Aucune vidéo. Uploade un fichier pour générer URL + thumbnail.
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFile}
        className="hidden"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handlePick}
          disabled={busy}
          className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black disabled:opacity-40"
        >
          {busy ? "Upload…" : tp.video_url ? "↻ Remplacer la vidéo" : "+ Upload vidéo"}
        </button>
        {status && (
          <span
            className={`font-mono text-[10px] ${
              status.startsWith("❌")
                ? "text-red-600"
                : status.startsWith("✓")
                  ? "text-green-700"
                  : "text-concrete"
            }`}
          >
            {status}
          </span>
        )}
      </div>
    </div>
  );
}

function ThumbnailPickerCard({
  tp,
  onPicked,
}: {
  tp: Touchpoint;
  onPicked: (thumbUrl: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const handleSnap = async () => {
    const v = videoRef.current;
    if (!v || !tp.video_url) return;
    setBusy(true);
    setStatus(`Capture à ${v.currentTime.toFixed(2)}s…`);
    try {
      const newThumbUrl = await captureAndUploadThumbnailAt(
        tp.video_url,
        v.currentTime,
        tp.id,
      );
      onPicked(newThumbUrl);
      setStatus(`✓ Thumbnail capturée à ${v.currentTime.toFixed(2)}s`);
    } catch (e: any) {
      console.error(e);
      setStatus(`❌ ${e?.message ?? "Capture échouée"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-stone rounded-lg p-3 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[10px] uppercase text-concrete">
          Choisir une thumbnail précise
        </span>
        <span className="font-mono text-[9px] text-ash italic">
          Avance la vidéo et capture la frame qui te plaît
        </span>
      </div>
      <video
        ref={videoRef}
        src={tp.video_url ?? undefined}
        crossOrigin="anonymous"
        controls
        playsInline
        muted
        className="w-full max-h-[300px] rounded border border-stone bg-black mb-2"
        style={{ aspectRatio: "16/9", objectFit: "contain" }}
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleSnap}
          disabled={busy}
          className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black disabled:opacity-40"
        >
          {busy ? "Capture…" : "📸 Capturer cette frame"}
        </button>
        {status && (
          <span
            className={`font-mono text-[10px] ${
              status.startsWith("❌")
                ? "text-red-600"
                : status.startsWith("✓")
                  ? "text-green-700"
                  : "text-concrete"
            }`}
          >
            {status}
          </span>
        )}
        {tp.video_thumb_url && (
          <img
            src={tp.video_thumb_url}
            alt="thumbnail actuelle"
            className="ml-auto w-20 h-12 object-cover rounded border border-stone"
            title="Thumbnail actuelle"
          />
        )}
      </div>
    </div>
  );
}

function AvatarUploadCard({
  tp,
  onUploaded,
}: {
  tp: Touchpoint;
  onUploaded: (url: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Choisis une image (PNG/JPG/WebP).");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert("Image trop grosse (max 20 MB).");
      return;
    }
    setBusy(true);
    setStatus("Upload…");
    try {
      const url = await uploadAvatar(file, tp.id);
      onUploaded(url);
      setStatus(`✓ Avatar uploadé`);
    } catch (err: any) {
      console.error(err);
      setStatus(`❌ ${err?.message ?? "Upload échoué"}`);
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="border border-stone rounded-lg p-3 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <span className="font-mono text-[10px] uppercase text-concrete">
          Avatar (le rond en bas-gauche de la vidéo)
        </span>
        <span className="font-mono text-[9px] text-ash italic">
          .jpg/.png/.webp · max 20 MB · carré recommandé
        </span>
      </div>
      <div className="flex items-center gap-3 mb-2">
        {tp.avatar_url ? (
          <img
            src={tp.avatar_url}
            alt="avatar"
            className="w-16 h-16 rounded-full object-cover border-2 border-charcoal"
          />
        ) : (
          <div className="w-16 h-16 rounded-full border-2 border-stone flex items-center justify-center font-bold text-2xl text-black"
               style={{ background: "linear-gradient(135deg, #d4a884, #7a6553)" }}>
            J
          </div>
        )}
        <div className="flex-1 text-xs font-mono text-concrete truncate">
          {tp.avatar_url ?? "Pas d'avatar — fallback initiale 'J' par défaut"}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        className="hidden"
      />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handlePick}
          disabled={busy}
          className="px-3 py-1.5 bg-charcoal text-ivory rounded text-xs font-mono uppercase hover:bg-black disabled:opacity-40"
        >
          {busy ? "Upload…" : tp.avatar_url ? "↻ Remplacer l'avatar" : "+ Upload avatar"}
        </button>
        {status && (
          <span
            className={`font-mono text-[10px] ${
              status.startsWith("❌")
                ? "text-red-600"
                : status.startsWith("✓")
                  ? "text-green-700"
                  : "text-concrete"
            }`}
          >
            {status}
          </span>
        )}
      </div>
    </div>
  );
}

function TargetsTab({ tpId }: { tpId: string }) {
  const [included, setIncluded] = useState<EligibleLead[]>([]);
  const [excluded, setExcluded] = useState<EligibleLead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!crmSupabase) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      const { data: sends } = await crmSupabase
        .from("touchpoint_send")
        .select("lead_id")
        .eq("touchpoint_id", tpId)
        .in("status", ["sent", "sent_manually"]);
      const alreadyReceived = new Set((sends ?? []).map((s: any) => s.lead_id));
      const { data: leads } = await crmSupabase
        .from("lead")
        .select(
          "id,full_name,is_estrella,is_test,status,category:category_id(name,color_hex)",
        )
        .in("status", ["active", "responded"])
        .eq("is_test", false);
      const inc: EligibleLead[] = [];
      const exc: EligibleLead[] = [];
      for (const l of (leads as any[]) ?? []) {
        if (alreadyReceived.has(l.id)) exc.push(l);
        else inc.push(l);
      }
      inc.sort(
        (a, b) =>
          Number(b.is_estrella) - Number(a.is_estrella) ||
          a.full_name.localeCompare(b.full_name),
      );
      setIncluded(inc);
      setExcluded(exc);
      setLoading(false);
    })();
  }, [tpId]);

  if (loading) {
    return <p className="font-mono text-sm text-concrete">Calcul des cibles…</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-mono text-[10px] uppercase text-concrete mb-2">
          Éligibles ({included.length})
        </h4>
        <ul className="grid grid-cols-2 gap-1 max-h-[400px] overflow-y-auto">
          {included.map((l) => (
            <li
              key={l.id}
              className="flex items-center gap-2 text-sm px-2 py-1 bg-ivory rounded"
            >
              {l.is_estrella && <span className="text-lemonade">★</span>}
              <span className="text-charcoal truncate">{l.full_name}</span>
              {l.category && (
                <span
                  className="font-mono text-[9px] uppercase px-1 py-0.5 rounded text-white ml-auto"
                  style={{ backgroundColor: l.category.color_hex }}
                >
                  {l.category.name.slice(0, 12)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-mono text-[10px] uppercase text-concrete mb-2">
          Déjà reçu ({excluded.length}) — exclus du prochain batch (anti-doublon)
        </h4>
        <ul className="flex flex-wrap gap-1">
          {excluded.map((l) => (
            <li
              key={l.id}
              className="text-xs px-2 py-0.5 bg-stone text-concrete rounded line-through"
            >
              {l.full_name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
